import { useState, useEffect, useRef } from 'react';
import { trpc } from '../../api/trpc';

interface AuditChatFilterProps {
  conversationId: string;
  conversationTitle: string;
  onChange: (value: { conversationId: string; conversationTitle: string }) => void;
}

/** A chat returned by `audit.searchChats` (the api-gateway audit client is untyped). */
interface ChatSearchResult {
  conversationId: string;
  title: string | null;
}

/**
 * Searchable chat picker for the audit log filter. Types a chat title into a
 * debounced search box backed by `audit.searchChats` (tenant-wide chat-title
 * search). Selecting a chat sets both the id (the functional filter) and the
 * title (for the chip label).
 */
export function AuditChatFilter({ conversationId, conversationTitle, onChange }: AuditChatFilterProps) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounce the typed query (matches the 300ms used by the text search).
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);

  // Close the dropdown when clicking outside the field.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const { data: results, isFetching } = trpc.audit.searchChats.useQuery(
    { query: debounced, limit: 20 },
    { enabled: open && debounced.length > 0 },
  );

  // A chat is already selected — show it with a clear control.
  if (conversationId) {
    return (
      <div className="audit-filter-field">
        <span className="audit-filter-label">Chat</span>
        <div className="audit-chat-selected">
          <span className="audit-chat-selected-title" title={conversationId}>
            {conversationTitle || conversationId}
          </span>
          <button
            className="audit-chat-clear"
            onClick={() => { onChange({ conversationId: '', conversationTitle: '' }); setInput(''); }}
            aria-label="Clear chat filter"
          >
            {'×'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-filter-field" ref={wrapRef}>
      <span className="audit-filter-label">Chat</span>
      <div className="audit-chat-filter">
        <input
          type="text"
          className="audit-filter-input"
          placeholder="Search chats by title..."
          value={input}
          onChange={e => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {open && debounced.length > 0 && (
          <div className="audit-chat-dropdown">
            {isFetching && <div className="audit-chat-dropdown-empty">Searching{'…'}</div>}
            {!isFetching && results && results.length === 0 && (
              <div className="audit-chat-dropdown-empty">No chats match that title.</div>
            )}
            {!isFetching && results && (results as ChatSearchResult[]).map(chat => (
              <button
                key={chat.conversationId}
                className="audit-chat-dropdown-item"
                onClick={() => {
                  onChange({ conversationId: chat.conversationId, conversationTitle: chat.title ?? '' });
                  setInput('');
                  setOpen(false);
                }}
              >
                <span className="audit-chat-dropdown-title">{chat.title || 'Untitled chat'}</span>
                <span className="audit-chat-dropdown-id">{chat.conversationId}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
